package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Layout
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.SettingsClasses
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.dom.Div

internal const val MEMBERS_GRID_COLUMNS =
    "minmax(0, 2.6fr) 84px 124px 112px 96px 148px"

object MembersClasses {
    const val ROW = "sb-members-row"
    const val HEADER = "sb-members-header"
    const val PERSON = "sb-members-person"
    const val ROLE = "sb-members-role"
    const val ACCOUNTS = "sb-members-accounts"
    const val STATUS = "sb-members-status"
    const val JOINED = "sb-members-joined"
    const val ACTIONS = "sb-members-actions"
}

private val phoneMedia = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointMd - 1}px)")

private val phoneAreas = listOf(
    MembersClasses.PERSON to "person",
    MembersClasses.ROLE to "role",
    MembersClasses.ACCOUNTS to "accounts",
    MembersClasses.STATUS to "status",
    MembersClasses.JOINED to "joined",
    MembersClasses.ACTIONS to "actions",
)

@InitSilk
fun initMembersTableStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyle(".${MembersClasses.ROW}") {
        base {
            Modifier.styleModifier {
                property("display", "grid")
                property("grid-template-columns", MEMBERS_GRID_COLUMNS)
                property("align-items", "center")
                property("column-gap", Spacing.md.toString())
            }
        }
        cssRule(phoneMedia, null) {
            Modifier.styleModifier {
                property("grid-template-columns", "minmax(0, 1fr) auto")
                property("grid-template-areas", "'person person' 'role role' 'accounts accounts' 'status joined' 'actions actions'")
                property("row-gap", Spacing.xs.toString())
            }
        }
    }
    ctx.stylesheet.registerStyle(".${MembersClasses.HEADER}") {
        base {
            Modifier.styleModifier {
                property("display", "grid")
                property("grid-template-columns", MEMBERS_GRID_COLUMNS)
                property("align-items", "center")
                property("column-gap", Spacing.md.toString())
            }
        }
        cssRule(phoneMedia, null) { Modifier.styleModifier { property("display", "none") } }
    }
    phoneAreas.forEach { (className, area) ->
        ctx.stylesheet.registerStyle(".$className") {
            base {
                Modifier.styleModifier {
                    if (className == MembersClasses.JOINED) property("justify-self", "end")
                }
            }
            cssRule(phoneMedia, null) { Modifier.styleModifier { property("grid-area", area) } }
        }
    }
    ctx.stylesheet.registerStyle(".${MembersClasses.ACCOUNTS}:empty") {
        cssRule(phoneMedia, null) { Modifier.styleModifier { property("display", "none") } }
    }
}

internal val MEMBERS_ROW_MIN_HEIGHT = "calc(${Spacing.xxl} + ${Spacing.xs})"

private const val SKELETON_ROWS = 4

private val MEMBERS_COLUMN_LABELS = listOf("Person", "Role", "Accounts", "Status", "Joined", "")

private const val JOINED_COLUMN = 4

@Composable
internal fun MembersWideSection(
    anchor: String,
    title: String,
    description: String? = null,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .classNames(Motion.ENTER_CLASS)
            .padding(topBottom = Spacing.xl)
            .styleModifier { property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}") },
    ) {
        Div(attrs = Modifier.fillMaxWidth().toAttrs { attr("id", settingsAnchorId(anchor)) }) {
            Column(modifier = Modifier.margin(bottom = Spacing.md).styleModifier { property("min-width", "0") }) {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary),
                )
                if (description != null) {
                    SpanText(
                        text = description,
                        modifier = Modifier
                            .margin(top = Spacing.xxs)
                            .fontSize(Typography.label.size)
                            .color(Palette.textSecondary)
                            .styleModifier { property("max-width", "64ch") },
                    )
                }
            }
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("min-width", "0") }) { content() }
        }
    }
}

@Composable
internal fun MembersTableHeader() {
    Row(
        modifier = Modifier
            .classNames(MembersClasses.HEADER)
            .fillMaxWidth()
            .padding(bottom = Spacing.xs)
            .styleModifier { property("border-bottom", "1px solid ${Palette.border.cssRgb()}") },
    ) {
        MEMBERS_COLUMN_LABELS.forEachIndexed { index, label ->
            SpanText(
                text = label,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .fontWeight(400)
                    .color(Palette.textTertiary)
                    .styleModifier {
                        if (index == JOINED_COLUMN) {
                            property("justify-self", "end")
                            property("text-align", "right")
                        }
                    },
            )
        }
    }
}

@Composable
internal fun MembersSkeletonRows() {
    repeat(SKELETON_ROWS) {
        Row(
            modifier = Modifier
                .classNames(MembersClasses.ROW)
                .fillMaxWidth()
                .padding(topBottom = Spacing.xs)
                .styleModifier {
                    property("min-height", MEMBERS_ROW_MIN_HEIGHT)
                    property("border-bottom", "1px solid ${Palette.borderSubtle.cssRgb()}")
                },
        ) {
            Row(modifier = Modifier.gap(Spacing.sm), verticalAlignment = Alignment.CenterVertically) {
                SkeletonBar(width = "${Space.s7}", height = "${Space.s7}", pill = true)
                Column(modifier = Modifier.gap(Spacing.xxs)) {
                    SkeletonBar(width = "${Space.s12}", height = "${Spacing.sm}")
                    SkeletonBar(width = "${Space.s11}", height = "${Spacing.sm}")
                }
            }
            repeat(MEMBERS_COLUMN_LABELS.size - 2) {
                SkeletonBar(width = "${Space.s8}", height = "${Spacing.sm}")
            }
            Box { }
        }
    }
}

@Composable
private fun SkeletonBar(width: String, height: String, pill: Boolean = false) {
    Box(
        modifier = Modifier
            .classNames(SettingsClasses.BAR)
            .styleModifier {
                property("width", width)
                property("height", height)
                if (pill) property("border-radius", Radius.pill.toString())
            },
    )
}
